import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { TableHeaderDirective } from '@app/shared/components/organisms/table/directives/table-header.directive';
import { TableCellDirective } from '@app/shared/components/organisms/table/directives/table-cell.directive';
import { RowCellsDirective } from '@app/shared/components/organisms/table/directives/row-cells.directive';
import { RowHeadersDirective } from '@app/shared/components/organisms/table/directives/row-headers.directive';
import { ColumnHeaderDirective } from '@app/shared/components/organisms/table/directives/column-header.directive';
import { distinctUntilChanged, map, ReplaySubject, Subject, take, takeUntil } from 'rxjs';
import { Pageable } from '@app/shared/interfaces/pagination.interface';
import { CollapseRowDirective } from '@app/shared/components/organisms/table/directives/collapse-row.directive';
import { StickyDirective } from '@app/shared/components/organisms/table/directives/sticky.directive';
import { MESSAGES } from '@app/shared/utils/messages';
import { CommonModule } from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TableDirective } from '@app/shared/components/organisms/table/directives/table.directive';
import { TableFooterComponent } from './table-footer/table-footer.component';

interface HeaderOption {
  name?: string;
  sort?: 'asc' | 'desc';
  field?: string;
  type?: 'filter' | 'sort';
  lastModifiedDate: number;
  values?: string[];
}

type TemplateBlock = 'loadingBlock' | 'messageBlock' | 'dataBlock';

type GroupByColumn = { [key: string]: StickyDirective[] };

@Component({
  selector: 'app-base-table',
  templateUrl: 'base-table.component.html',
  styleUrls: ['./base-table.component.scss'],
  imports: [CommonModule, TableFooterComponent, NgxSkeletonLoaderModule, TableDirective],
  standalone: true,
})
export class BaseTableComponent<T> implements AfterViewInit, OnInit, AfterContentChecked, OnDestroy, OnChanges {
  private unsubscribe$: Subject<boolean> = new Subject<boolean>();

  @Input() functionOnSort!: ((param: any, sort: 'asc' | 'desc' | undefined, field?: unknown) => void) | null;

  @Input() isLoading = true;

  @Input() showFooter = true;

  @Input() id = 'default-table-id';

  @Input() source: T[] = [];

  @Input() pageable?: Pageable<T>;

  @Input() limits?: number[];

  @Output() protected pageableChange = new EventEmitter<Pageable<T>>();

  @Input() displayedColumns?: string[];

  @Input() messageInfo: string = '';

  @Input() tableLayoutAuto: boolean = false;

  @Input() fieldSortDifferently!: string[];

  public displayedSource: T[] = [];

  protected displayedPage: T[] = [];

  protected rows: number[] = [];

  protected headerRows: number[] = Array.from(Array(1).keys());

  @ContentChildren(forwardRef(() => ColumnHeaderDirective))
  private columns!: QueryList<ColumnHeaderDirective>;

  @ContentChildren(TableHeaderDirective)
  protected headers!: QueryList<TableHeaderDirective>;

  @ContentChildren(TableCellDirective)
  protected cells!: QueryList<TableCellDirective<T>>;

  @ContentChildren(StickyDirective)
  private stickyElements!: QueryList<StickyDirective>;

  @ContentChild(RowHeadersDirective)
  private rowsHeaders!: RowHeadersDirective;

  @ContentChild(RowCellsDirective)
  private rowsCells!: RowCellsDirective;

  @ContentChild(CollapseRowDirective)
  private collapseRows!: CollapseRowDirective;

  @ViewChildren('headersVcr', { read: ViewContainerRef })
  private headersVcr!: QueryList<ViewContainerRef>;

  @ViewChildren('rowsVcr', { read: ViewContainerRef })
  private rowsVcr!: QueryList<ViewContainerRef>;

  @ViewChild('tableContainer')
  public readonly tableContainer!: ElementRef;

  private trViewRefs: EmbeddedViewRef<any>[] = [];

  private columnsEvent = new ReplaySubject<any>(1);

  headerFilters: HeaderOption[] = [];

  protected readonly Array = Array;

  protected limit: number = 10;

  protected currentPage: number = 1;

  noFilteredDataMessage: { title: string; imgSrc: string } = {
    title: MESSAGES.NO_DATA_ERROR,
    imgSrc: 'assets/no_data.svg',
  };

  constructor(
    private renderer: Renderer2,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const [limit] = this.limits || [this.limit];
    this.limit = limit;

    this.displayedSource = [...this.source];

    this.columnsEvent
      .pipe(
        takeUntil(this.unsubscribe$),
        map((columnEvent) => this.mapOptions(columnEvent)),
        distinctUntilChanged((prev, curr) => {
          const prevShowColumn = JSON.stringify(prev);
          const currShowColumn = JSON.stringify(curr);

          return prevShowColumn === currShowColumn;
        }),
      )
      .subscribe({
        next: (headerOptions: HeaderOption[]) => {
          this.headerFilters = headerOptions;

          this.currentPage = 1;

          this.updateTable();
          this.changeDetectorRef.detectChanges();
        },
      });
  }

  ngAfterContentChecked(): void {
    const { columnsEvent, stickyElements, columns } = this;

    columnsEvent.next(columns);

    if (stickyElements) this.initStickyElements();
  }

  private initStickyElements() {
    const accPosition: GroupByColumn = {};

    this.stickyElements.reduce((acc, item) => {
      const { columnName } = item;
      if (acc[columnName]) acc[columnName].push(item);
      else acc[columnName] = [item];

      return acc;
    }, accPosition);

    const position = { left: 0, right: 0 };
    const shadowBoxColumns = this.getExtremityColumns(accPosition);

    Object.entries(accPosition).forEach(([, stickyElements]) => {
      const [item] = stickyElements;
      const { offsetWidth } = item.el.nativeElement;

      stickyElements.forEach((directive) => {
        if (directive.isRightDirection()) directive.setRight(position.right);
        else directive.setLeft(position.left);

        if (shadowBoxColumns.includes(directive.columnName)) directive.addBoxShadow();
      });

      if (item.isRightDirection()) position.right += offsetWidth;
      else position.left += offsetWidth;
    });
  }

  private getExtremityColumns(position: GroupByColumn): string[] {
    const groupByDirection: any = { left: [], right: [] };

    Object.entries(position)
      .map(([name, [element]]) => {
        const direction = element.isRightDirection() ? 'right' : 'left';
        return [name, direction];
      })
      .reduce((acc, [name, direction]) => {
        if (acc[direction]) acc[direction].push(name);
        else acc[direction] = [name];

        return acc;
      }, groupByDirection);

    const { left, right } = groupByDirection;
    return [left.pop(), right.shift()];
  }

  private removePreviousSort() {
    const [, ...previousSorts] = this.headerFilters
      .filter(({ type }) => type === 'sort')
      .filter(({ lastModifiedDate }) => lastModifiedDate > 0)
      .reverse();

    const columnNames = previousSorts.map(({ name }) => name);

    this.columns &&
      this.columns
        .filter((column) => column.header !== undefined)
        .filter(({ header }) => columnNames.includes(header!.name))
        .forEach((column) => column.removeSort());
  }

  private mapOptions(columns: QueryList<ColumnHeaderDirective>): HeaderOption[] {
    return columns
      .filter((column) => column.header !== undefined)
      .map(({ header }) => header!)
      .filter(({ field }) => ![field].includes(undefined))
      .sort((a, b) => a.lastModifiedDate - b.lastModifiedDate)
      .map((header) => header.extractOptions());
  }

  private sortTable() {
    const { headerFilters } = this;

    this.removePreviousSort();

    headerFilters
      .filter(({ type, sort }) => type === 'sort' && sort !== undefined)
      .forEach(({ field, sort }) => {
        if (this.fieldSortDifferently?.includes(<string>field)) {
          this.functionOnSort?.(this.source, sort, field);
        } else {
          this.source = this.source.sort((first: T, second: T) => {
            const firstValue = this.searchFieldValue(first, field!);

            const secondValue = this.searchFieldValue(second, field!);

            const isAsc = sort === 'asc' ? 1 : -1;

            if (firstValue === secondValue) return 0;
            const sortValue = firstValue > secondValue ? 1 : -1;
            return isAsc * sortValue;
          });
        }
      });
  }

  private filterTable() {
    const { headerFilters, source } = this;
    const filters = headerFilters.filter(({ type }) => type === 'filter');

    this.displayedSource = [...source];

    if (!filters.length) {
      return;
    }

    filters.forEach(({ field, values }) => {
      this.displayedSource = this.displayedSource.filter((item) => {
        if (!values || !values.length) return true;
        const value = this.searchFieldValue(item, field!);
        return values?.map((v) => v.toLowerCase()).includes(value);
      });
    });

    this.updateFiltersCount();
  }

  private updateFiltersCount() {
    const [, ...previousSorts] = this.headerFilters.filter(({ type }) => type === 'filter').reverse();

    const columnNames = previousSorts.map(({ name }) => name);

    this.columns
      .filter((column) => column.header !== undefined)
      .filter(({ header }) => columnNames.includes(header!.name))
      .map((column) => column.header!.updateCount());
  }

  get isFiltered() {
    return !!this.headerFilters.filter(({ type, values }) => type === 'filter' && values?.length).length;
  }

  private searchFieldValue(obj: any, path: string) {
    let current = obj;
    path.split('.').forEach((p) => {
      current = current[p];
    });

    if (typeof current === 'string' || current instanceof String) return current.toLowerCase();

    return current;
  }

  ngAfterViewInit() {
    const { rowsVcr, headersVcr } = this;

    headersVcr.changes.pipe(take(1)).subscribe({
      next: () => this.updateHeaders(),
    });

    rowsVcr.changes.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: () => this.updateRows(),
    });

    this.updateTable();

    this.changeDetectorRef.detectChanges();
  }

  private updateTable() {
    this.sortTable();
    this.filterTable();
    this.updatePagination();

    // Réinitialiser les vues précédentes
    this.cleanTableViews();

    // Mettre à jour les vues avec les nouvelles données
    this.displayedPage = this.paginate(this.displayedSource);
    this.rows = Array.from(Array(this.displayedPage.length).keys());

    this.headersVcr?.notifyOnChanges();
    this.rowsVcr?.notifyOnChanges();

    this.changeDetectorRef.detectChanges();
  }

  private paginate(data: T[]): T[] {
    const start = (this.currentPage - 1) * this.limit;
    const end = start + this.limit;
    return data.slice(start, end);
  }

  private updatePagination() {
    const { limit, currentPage, pageable } = this;

    if (pageable) {
      this.displayedPage = this.displayedSource;
      return;
    }

    const start = (currentPage - 1) * limit;
    const end = currentPage * limit;

    this.displayedPage = this.displayedSource.slice(start, end);
  }

  private updateHeaders() {
    const { headersVcr, headers, rowsHeaders } = this;

    if (!rowsHeaders) return;

    headersVcr!.forEach((vcr) => {
      const trView = vcr.createEmbeddedView(rowsHeaders.templateRef);
      const [tr] = trView.rootNodes;

      headers.forEach((directive) => {
        const thView = vcr!.createEmbeddedView(directive.templateRef);
        const [th] = thView.rootNodes;

        this.renderer.appendChild(tr, th);
      });
    });
  }

  private updateRows() {
    if (!this.rowsCells || !this.rowsVcr) return;

    this.cleanTableViews();

    this.rowsVcr!.forEach((vcr, index) => {
      const $implicit = this.displayedPage[index];
      if (!$implicit) return;

      const context = { $implicit, index };
      const trView = vcr.createEmbeddedView(this.rowsCells.templateRef, context);
      this.trViewRefs.push(trView);
      const [tr] = trView.rootNodes;

      this.cells.forEach((directive) => {
        const tdView = vcr.createEmbeddedView(directive.templateRef, context);
        const [td] = tdView.rootNodes;
        this.renderer.appendChild(tr, td);
      });
    });
  }

  private cleanTableViews() {
    this.trViewRefs.forEach((trViewRef) => trViewRef.destroy());
    this.trViewRefs = [];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.source && this.source && this.source.length > 0) {
      const { currentValue, previousValue } = changes.source;
      if (JSON.stringify(currentValue) !== JSON.stringify(previousValue)) {
        this.updateTable();
      }
    }
    if (changes.source && this.source && this.source.length == 0) {
      this.displayedSource = this.source;
    }
  }

  protected get currentTemplate(): TemplateBlock {
    const { isLoading, messageInfo, source, displayedSource } = this;
    if (isLoading) return 'loadingBlock';
    if ((messageInfo && !source.length) || !displayedSource.length) return 'messageBlock';
    return 'dataBlock';
  }

  get displayableRows() {
    return this.displayedSource;
  }

  onLimitChange(limit: number) {
    const { pageable } = this;

    if (!pageable) {
      this.updateTable();
      return;
    }

    this.pageableChange.emit({ ...pageable, limit, offset: 0 });
  }

  onPageChange(currentPage: number) {
    const { pageable } = this;

    if (!pageable) {
      this.updateTable();
      return;
    }

    const offset = (currentPage - 1) * pageable.limit;

    this.pageableChange.emit({ ...pageable, offset });
  }

  resetPage() {
    this.currentPage = 1;
    this.pageableChange.emit();
  }

  get hasCollapseRow(): boolean {
    return this.collapseRows !== undefined;
  }

  ngOnDestroy(): void {
    this.columnsEvent.complete();
    this.unsubscribe$.next(true);
  }
}
